import { KeyboardEvent, useCallback, useState } from 'react';
import { SelectMenuCreateAction } from './select-menu.types';

interface UseCreateActionFocusParams {
  createAction: SelectMenuCreateAction | undefined;
  activeQuery: string;
}

interface CreateActionFocus {
  isCreateActionFocused: boolean;
  resetCreateActionFocus: () => void;
  /** Returns `true` when the key was consumed by the create-action row. */
  handleCreateActionKeyDownCapture: (event: KeyboardEvent<HTMLElement>) => boolean;
}

const consumeKey = (event: KeyboardEvent<HTMLElement>) => {
  event.preventDefault();
  event.stopPropagation();
};

// Virtual highlight for `createAction`: cmdk owns real item highlighting
// internally and never moves DOM focus off its search input, so the
// pinned create-action row is tracked the same way (a flag, not focus())
// instead of participating in cmdk's own item/value state.
export const useCreateActionFocus = ({
  createAction,
  activeQuery,
}: UseCreateActionFocusParams): CreateActionFocus => {
  const [isCreateActionFocused, setIsCreateActionFocused] = useState(false);

  // A new query rebuilds the list the highlight was placed against, so it is
  // dropped during render rather than in an Effect (no external system here).
  const [previousQuery, setPreviousQuery] = useState(activeQuery);
  if (previousQuery !== activeQuery) {
    setPreviousQuery(activeQuery);
    setIsCreateActionFocused(false);
  }

  const resetCreateActionFocus = useCallback(() => setIsCreateActionFocused(false), []);

  const handleCreateActionKeyDownCapture = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      if (!createAction) {
        return false;
      }

      if (event.key === 'ArrowDown') {
        // Already on the create-action row: swallow further ArrowDowns
        // instead of letting them reach cmdk, which would otherwise move
        // its own (now-hidden) highlight out from under this row.
        if (isCreateActionFocused) {
          consumeKey(event);
          return true;
        }
        const items = event.currentTarget.querySelectorAll<HTMLElement>(
          '[cmdk-item]:not([aria-disabled="true"])',
        );
        const selectedIndex = Array.from(items).findIndex(
          (item) => item.getAttribute('aria-selected') === 'true',
        );
        const isAtOrPastLastItem = items.length === 0 || selectedIndex === items.length - 1;
        if (isAtOrPastLastItem) {
          consumeKey(event);
          setIsCreateActionFocused(true);
          return true;
        }
        return false;
      }

      if (event.key === 'ArrowUp' && isCreateActionFocused) {
        consumeKey(event);
        setIsCreateActionFocused(false);
        return true;
      }

      if (event.key === 'Enter' && isCreateActionFocused) {
        consumeKey(event);
        createAction.onSelect();
        return true;
      }

      return false;
    },
    [createAction, isCreateActionFocused],
  );

  return {
    isCreateActionFocused,
    resetCreateActionFocus,
    handleCreateActionKeyDownCapture,
  };
};
